// React
import React, { useEffect, useRef, useState } from "react";

// External libraries
import { faInfoCircle } from "@fortawesome/sharp-solid-svg-icons";
import ReactDOM from "react-dom";

// Internal
import { Button } from "../buttons/button";
import { CloseButton } from "../buttons/close-button.jsx";
import { getDataTestId } from "../functions/data-test-ids";
import { useOptionalHelpContext } from "../help-context";
import { FontAwesome } from "../icons/icons";

// CSS
import "./tooltip.css";

const Markdown = React.lazy(() => import("../markdown/markdown"));

export const TooltipDropDirection = {
    UpLeft: 1,
    UpRight: 2,
    DownLeft: 3,
    DownRight: 4
}

export const TooltipWidth = {
    Default: 300,
    Large: 500
}

const DefaultTooltipWidth = 300;

function TooltipFooter(props) {
    const navigateToHelp = () => {
        if (typeof props.ShowHelp === "function") {
            props.ShowHelp({ Type: "Article", Id: props.Article });
        }
    };

    if (!props.Article) {
        return null;
    }

    return (
        <div className="eone-ui-tooltip-footer">
            <Button
                Label={"More information"}
                OnClick={navigateToHelp}
                DataTestId={getDataTestId(props.DataTestId, "tooltip", "read-more")} />
        </div>
    );
}

function TooltipWindow(props) {
    // Already called through optional chaining below; a host with no provider shows the
    // tooltip without its Read more footer.
    const helpContext = useOptionalHelpContext();

    const showHelp = (article) => {
        helpContext?.push(article);
        props.Close();
    };

    if (!props.Show || !props.Style) {
        return null;
    }
    return ReactDOM.createPortal(
        <div
            ref={props.TooltipRef}
            data-testid={getDataTestId(props.DataTestId, "tooltip")}
            className="eone-ui-tooltip"
            style={props.Style}
            data-direction={props.Direction}
            onMouseEnter={props.OnMouseEnter}
            onMouseLeave={props.OnMouseLeave}>
            {props.Title && (
                <div className="eone-ui-tooltip-header">
                    <div className="eone-ui-tooltip-title" data-testid={getDataTestId(props.DataTestId, "tooltip", "title")}>{props.Title}</div>
                    <CloseButton
                        Visible={true}
                        Close={props.Close}
                        DataTestId={getDataTestId(props.DataTestId, "tooltip", "header-close-button")} />
                </div>
            )}
            <div className="eone-ui-tooltip-content" data-testid={getDataTestId(props.DataTestId, "tooltip", "content")}>
                <React.Suspense fallback={<div className="eone-ui-tooltip-loading" />}>
                    <Markdown>
                        {props.Tooltip.Message}
                    </Markdown>
                </React.Suspense>
            </div>
            <TooltipFooter
                Article={props.Tooltip.RelatedArticle}
                ShowHelp={showHelp}
                Close={props.Close}
                DataTestId={props.DataTestId} />
        </div>,
        document.body
    );
}

function getStyleAndDirection(buttonEl, tooltipWidth) {
    const button = buttonEl.getBoundingClientRect();
    const downHeightAvailable = window.innerHeight - button.bottom;
    const rightWidthAvailable = window.innerWidth - button.left;

    const isLeft = rightWidthAvailable < 315;
    const isUp = downHeightAvailable < 315;
    const direction = `${isUp ? "up" : "down"}-${isLeft ? "left" : "right"}`;
    const width = tooltipWidth || DefaultTooltipWidth;

    if (isLeft) {
        if (isUp) {
            return {
                style: {
                    bottom: window.innerHeight - button.top + 10,
                    right: window.innerWidth - button.right,
                    maxWidth: width,
                    maxHeight: 300,
                    width,
                },
                direction,
            };
        }
        return {
            style: {
                top: button.bottom + 10,
                right: window.innerWidth - button.right,
                maxWidth: width,
                maxHeight: 300,
                width,
            },
            direction,
        };
    }
    if (isUp) {
        return {
            style: {
                bottom: window.innerHeight - button.top + 10,
                left: button.left,
                maxWidth: rightWidthAvailable,
                maxHeight: 300,
                width,
            },
            direction,
        };
    }
    return {
        style: {
            top: button.bottom + 10,
            left: button.left,
            maxWidth: rightWidthAvailable,
            maxHeight: downHeightAvailable,
            width,
        },
        direction,
    };
}

function Tooltip(props) {
    const [show, setShow] = useState(false);
    const buttonRef = useRef(null);
    const tooltipRef = useRef(null);
    const [styleInfo, setStyleInfo] = useState(null);
    const hoverOpenTimer = useRef(null);
    const hoverCloseTimer = useRef(null);

    useEffect(() => {
        const handleDocumentClick = (e) => {
            if (!buttonRef.current) return;
            if (!tooltipRef.current) return;
            if (buttonRef.current.contains(e.target)) return;
            if (tooltipRef.current.contains(e.target)) return;
            close();
        };
        document.addEventListener("mousedown", handleDocumentClick);
        return () => {
            document.removeEventListener("mousedown", handleDocumentClick);
        };
    }, []);

    useEffect(() => {
        return () => {
            if (hoverOpenTimer.current) clearTimeout(hoverOpenTimer.current);
            if (hoverCloseTimer.current) clearTimeout(hoverCloseTimer.current);
        };
    }, []);

    const open = () => {
        if (!buttonRef.current) return;
        const info = getStyleAndDirection(buttonRef.current, props.TooltipWidth);
        setStyleInfo(info);
        setShow(true);
    };

    const close = () => setShow(false);

    const toggleOpen = () => {
        if (!show) {
            open();
        } else {
            close();
        }
    };

    const onIconMouseEnter = () => {
        if (!props.HoverMode) return;
        if (hoverCloseTimer.current) {
            clearTimeout(hoverCloseTimer.current);
            hoverCloseTimer.current = null;
        }
        hoverOpenTimer.current = setTimeout(open, props.HoverDelay ?? 400);
    };

    const onIconMouseLeave = () => {
        if (!props.HoverMode) return;
        if (hoverOpenTimer.current) {
            clearTimeout(hoverOpenTimer.current);
            hoverOpenTimer.current = null;
        }
        hoverCloseTimer.current = setTimeout(close, 150);
    };

    const onTooltipMouseEnter = () => {
        if (!props.HoverMode) return;
        if (hoverCloseTimer.current) {
            clearTimeout(hoverCloseTimer.current);
            hoverCloseTimer.current = null;
        }
    };

    const onTooltipMouseLeave = () => {
        if (!props.HoverMode) return;
        hoverCloseTimer.current = setTimeout(close, 150);
    };

    if (!props.Tooltip || !props.Tooltip.Message) {
        return null;
    }

    return (
        <div className="eone-ui-tooltip-container">
            <a
                ref={buttonRef}
                className="eone-ui-tooltip-icon"
                role="button"
                tabIndex={0}
                aria-label={props.Tooltip.Title || "More information"}
                onClick={toggleOpen}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleOpen(); } }}
                onMouseEnter={onIconMouseEnter}
                onMouseLeave={onIconMouseLeave}
                data-testid={getDataTestId(props.DataTestId, "tooltip", "button")}>
                <FontAwesome Icon={props.Icon ? props.Icon : faInfoCircle} IconStyle={props.IconStyle} FixedWidth />
            </a>
            <TooltipWindow
                Show={show}
                TooltipRef={tooltipRef}
                Title={props.Title}
                Tooltip={props.Tooltip}
                Style={styleInfo?.style}
                Direction={styleInfo?.direction}
                TooltipWidth={props.TooltipWidth}
                Close={close}
                OnMouseEnter={onTooltipMouseEnter}
                OnMouseLeave={onTooltipMouseLeave}
                DataTestId={props.DataTestId} />
        </div>
    );
}

export function OverflowTooltip({ children, Label, AlwaysShow }) {
    const triggerRef = useRef(null);
    const [show, setShow] = useState(false);
    const [style, setStyle] = useState(null);
    // Closing is deferred so the pointer can travel the small gap from the cell
    // onto the popup (which lives in a portal) without the popup vanishing. The
    // popup's own mouse handlers cancel this pending close, keeping it open — and
    // scrollable — while hovered.
    const closeTimer = useRef(null);

    useEffect(() => () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
    }, []);

    const cancelClose = () => {
        if (closeTimer.current) {
            clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
    };

    const scheduleClose = () => {
        cancelClose();
        closeTimer.current = setTimeout(() => setShow(false), 150);
    };

    const getRect = (el) => {
        // display:contents elements have no layout box — fall back to first child
        const rect = el.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0 && el.firstElementChild) {
            return el.firstElementChild.getBoundingClientRect();
        }
        return rect;
    };

    const getOverflowEl = (el) => {
        // display:contents wrapper has no dimensions — find the actual text-bearing element inside
        if (el.offsetWidth === 0 && el.offsetHeight === 0) {
            return el.querySelector("input, textarea") || el.firstElementChild || el;
        }
        return el;
    };

    const handleMouseEnter = () => {
        cancelClose();
        const el = triggerRef.current;
        if (!el) return;
        if (!AlwaysShow && getOverflowEl(el).scrollWidth <= getOverflowEl(el).offsetWidth) return;
        const rect = getRect(el);
        if (rect.width === 0 && rect.height === 0) return;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const isUp = spaceBelow < 80 || (spaceAbove > spaceBelow);
        setStyle(isUp
            ? { bottom: window.innerHeight - rect.top + 4, left: rect.left, maxHeight: Math.min(300, spaceAbove - 8) }
            : { top: rect.bottom + 4, left: rect.left, maxHeight: Math.min(300, spaceBelow - 8) }
        );
        setShow(true);
    };

    const handleMouseLeave = () => scheduleClose();

    const portal = show && style && ReactDOM.createPortal(
        <div
            className="eone-ui-overflow-tooltip"
            style={style}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}>
            <React.Suspense fallback={<div className="eone-ui-tooltip-loading" />}>
                <Markdown>{Label}</Markdown>
            </React.Suspense>
        </div>,
        document.body
    );

    // Native DOM elements (td, span, label, div…): inject ref and handlers directly
    if (typeof children.type === "string") {
        const child = React.cloneElement(children, {
            ref: triggerRef,
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave,
        });
        return <>{child}{portal}</>;
    }

    // React components (Button, InputString, etc.): wrap in a display:contents span.
    // display:contents has no layout box so mouseenter/leave never fire — use
    // mouseover/mouseout (which bubble from children) with a relatedTarget guard
    // to prevent flicker when the mouse moves between child elements.
    const handleMouseOver = (e) => {
        cancelClose();
        if (show) return;
        handleMouseEnter();
    };

    const handleMouseOut = (e) => {
        if (triggerRef.current?.contains(e.relatedTarget)) return;
        handleMouseLeave();
    };

    return (
        <span ref={triggerRef} className="eone-ui-overflow-tooltip-wrapper" onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
            {children}
            {portal}
        </span>
    );
}

export function LabelTooltip({ children, Tooltip }) {
    const triggerRef = useRef(null);
    const [show, setShow] = useState(false);
    const [style, setStyle] = useState(null);
    const openTimer = useRef(null);

    useEffect(() => {
        return () => {
            if (openTimer.current) clearTimeout(openTimer.current);
        };
    }, []);

    const handleMouseEnter = () => {
        openTimer.current = setTimeout(() => {
            const el = triggerRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const isUp = window.innerHeight - rect.bottom < 200;
            const rightAvailable = window.innerWidth - rect.left;
            const width = Math.min(300, rightAvailable);
            setStyle(isUp
                ? { bottom: window.innerHeight - rect.top + 4, left: rect.left, maxWidth: width, width }
                : { top: rect.bottom + 4, left: rect.left, maxWidth: width, width }
            );
            setShow(true);
        }, 300);
    };

    const handleMouseLeave = () => {
        if (openTimer.current) {
            clearTimeout(openTimer.current);
            openTimer.current = null;
        }
        setShow(false);
    };

    if (!Tooltip || !Tooltip.Message) {
        return children;
    }

    const child = React.cloneElement(children, {
        ref: triggerRef,
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
    });

    return (
        <>
            {child}
            {show && style && ReactDOM.createPortal(
                <div className="eone-ui-label-tooltip" style={style}>
                    <div className="eone-ui-tooltip-content">
                        <React.Suspense fallback={<div className="eone-ui-tooltip-loading" />}>
                            <Markdown>{Tooltip.Message}</Markdown>
                        </React.Suspense>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
}

export { Tooltip };
